import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Element, Character, Pillar } from '../types';
import { elementsAPI } from '../services/api';
// import { initializeTabSwitching } from '../utils/tabSwitching';
import { useConfirmModal } from '../hooks/useConfirmModal';
import { useOptimizedImage } from '../hooks/useOptimizedImage';
import ConfirmModal from './ConfirmModal';
import UploadImageModal from './UploadImageModal';
import SidePanel from './SidePanel';
import OptimizedImage from './OptimizedImage';
import LoadingOverlay from './LoadingOverlay';
import './ElementCard.css';
import { getImageSource, handleImageError } from '../utils/imageUtils';
import { useImageUpload } from '../hooks/useImageUpload';

// Create Template Form Component
interface CreateTemplateFormProps {
  field: 'article' | 'description';
  currentContent: string;
  onCreateTemplate: (name: string, content: string) => void;
  onCancel: () => void;
}

const CreateTemplateForm: React.FC<CreateTemplateFormProps> = ({
  field,
  currentContent,
  onCreateTemplate,
  onCancel
}) => {
  const [templateName, setTemplateName] = useState('');
  const [templateContent, setTemplateContent] = useState(currentContent);
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!templateName.trim() || !templateContent.trim()) return;
    
    setLoading(true);
    onCreateTemplate(templateName.trim(), templateContent.trim());
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="create-template-form">
      <div className="form-group">
        <label htmlFor="template-name">Template Name</label>
        <input
          id="template-name"
          type="text"
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          placeholder="Enter template name..."
          required
          autoFocus
        />
      </div>
      
      <div className="form-group">
        <label htmlFor="template-content">Template Content</label>
        <textarea
          id="template-content"
          value={templateContent}
          onChange={(e) => setTemplateContent(e.target.value)}
          placeholder="Enter template content..."
          required
          rows={10}
        />
        <small className="form-help">
          This will be the content that gets added when the template is selected.
        </small>
      </div>
      
      <div className="form-actions">
        <button type="button" className="btn btn--gray" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn--gold" disabled={loading || !templateName.trim() || !templateContent.trim()}>
          {loading ? 'Creating...' : 'Create Template'}
        </button>
      </div>
    </form>
  );
};

// Extend Window interface for global functions
declare global {
  interface Window {
    showToast: (message: string, type?: 'success' | 'error' | 'info' | 'warning', duration?: number) => void;
  }
}

interface ElementCardProps {
  element: Element;
  onUpdate: (element: Element) => void;
  onDelete: (elementId: string) => void;
  onOpenPreviewPanel: (elementIds: string[]) => void;
  defaultTab?: string;
}

const ElementCard: React.FC<ElementCardProps> = ({
  element,
  onUpdate,
  onDelete,
  onOpenPreviewPanel,
  defaultTab = 'summary'
}) => {
  // Debug: Log component mount
  // console.log('🔍 ElementCard MOUNTED for:', element.name, 'at', new Date().toISOString());
  // console.log('🔍 INITIAL VALUES - element.name:', element.name, 'activeTab will be:', 'summary');
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [showSaveCancel, setShowSaveCancel] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDeleteBtn, setShowDeleteBtn] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [droppedFile, setDroppedFile] = useState<File | null>(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageKey, setImageKey] = useState(0);
  const [showSidePanel, setShowSidePanel] = useState(false);
  const [sidePanelField, setSidePanelField] = useState('');
  const [sidePanelTitle, setSidePanelTitle] = useState('');
  const [showTextEditModal, setShowTextEditModal] = useState(false);
  const [textEditData, setTextEditData] = useState<{ field: string; title: string; content: string } | null>(null);
  const [showCreateTemplateModal, setShowCreateTemplateModal] = useState(false);
  const [customTemplates, setCustomTemplates] = useState<{
    article?: Array<{ name: string; content: string }>;
    description?: Array<{ name: string; content: string }>;
  }>({ article: [], description: [] });
  const [activeTab, setActiveTab] = useState<'summary' | 'features' | 'attributes' | 'linkedCharacters'>(defaultTab as 'summary' | 'features' | 'attributes' | 'linkedCharacters');
  // console.log('🔍 activeTab state initialized to:', activeTab);
  
  // Add state for collapsed sections and categories
  const [collapsedSections, setCollapsedSections] = useState<{
    equipment: boolean;
    details: boolean;
  }>({
    equipment: true,
    details: true
  });

  const [collapsedCategories, setCollapsedCategories] = useState<{
    weapons: boolean;
    armor: boolean;
    items: boolean;
    species: boolean;
    gender: boolean;
    beliefs: boolean;
    family: boolean;
    affiliations: boolean;
    abilities: boolean;
  }>({
    weapons: true,
    armor: true,
    items: true,
    species: true,
    gender: true,
    beliefs: true,
    family: true,
    affiliations: true,
    abilities: true
  });
  
  // Card loading hook (now using useOptimizedImage with overlay)
  const imageSrc = getImageSource(element, 'element', imageKey);
  const {
    cardRef,
    overlayRef,
    loadingState = { isLoading: false, progress: 0, error: false }
  } = useOptimizedImage({
    src: imageSrc,
    alt: element.name,
    withCardOverlay: false
  });
  
  const { modalState, showConfirm, handleConfirm, handleCancel: handleModalCancel } = useConfirmModal();

  // Use the standardized image upload hook
  const { handleImageUpload, uploading: uploadState } = useImageUpload({
    entityId: element._id,
    cardType: 'elements',
    entityName: element.name,
    onUpdate: onUpdate,
    setImageLoading: setImageLoading,
    setImageKey: setImageKey,
    setUploading: setUploading,
    showToast: window.showToast
  });

  const [showRarityMenu, setShowRarityMenu] = useState(false);
  const rarityMenuRef = useRef<HTMLDivElement>(null);
  const badgeRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState<{top: number, left: number} | null>(null);
  const [isRefreshingLinks, setIsRefreshingLinks] = useState(false);

  // Close menu on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (rarityMenuRef.current && !rarityMenuRef.current.contains(event.target as Node)) {
        setShowRarityMenu(false);
      }
    }
    if (showRarityMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showRarityMenu]);

  // Prevent scrolling when rarity menu is open
  useEffect(() => {
    if (showRarityMenu) {
      // Store the current scroll position
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;
      
      // Prevent scrolling by setting overflow hidden on body
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = `-${scrollX}px`;
      document.body.style.width = '100%';
      
      return () => {
        // Restore scrolling when menu closes
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.width = '';
        
        // Restore scroll position
        window.scrollTo(scrollX, scrollY);
      };
    }
  }, [showRarityMenu]);

  const openRarityMenu = () => {
    if (badgeRef.current) {
      const rect = badgeRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.top + rect.height / 2,
        left: rect.left + rect.width / 2
      });
    }
    setShowRarityMenu(true);
  };

  const handleRaritySelect = async (rarity: string) => {
    if (rarity === element.rarity) {
      setShowRarityMenu(false);
      return;
    }
    setLoading(true);
    try {
      const updatedElement = await elementsAPI.update(element._id, { rarity });
      // Defensive: ensure rarity is present
      onUpdate({ ...element, ...updatedElement, rarity });
      setShowRarityMenu(false);
      if (window.showToast) window.showToast('Rarity updated!', 'success');
    } catch (error) {
      if (window.showToast) window.showToast('Failed to update rarity', 'error');
      setShowRarityMenu(false);
    } finally {
      setLoading(false);
    }
  };

  // Initialize tab switching when component mounts
  // Note: ElementCard uses React state for tab switching, not DOM-based switching
  // useEffect(() => {
  //   initializeTabSwitching(element._id);
  // }, [element._id]);

  // Monitor element changes for debugging (only log when imageUrl changes)
  useEffect(() => {
    if (element.imageUrl && element.imageUrl.includes('amazonaws.com')) {
      // console.log('🔄 Element image updated:', {
      //   id: element._id,
      //   name: element.name,
      //   imageUrl: element.imageUrl.substring(0, 50) + '...'
      // });
    }
  }, [element.imageUrl]);

  // Update activeTab when defaultTab prop changes
  useEffect(() => {
    const validTabs = ['summary', 'features', 'attributes', 'linkedCharacters'];
    if (validTabs.includes(defaultTab)) {
      setActiveTab(defaultTab as 'summary' | 'features' | 'attributes' | 'linkedCharacters');
    }
  }, [defaultTab]);

  // Handle image refresh when element changes
  useEffect(() => {
    // If the element has a new image URL, refresh the image
    if (element.imageUrl && element.uploadTimestamp) {
      const currentTimestamp = element.uploadTimestamp;
      const lastTimestamp = element._id ? localStorage.getItem(`element_${element._id}_timestamp`) : null;
      
      if (lastTimestamp !== currentTimestamp.toString()) {
        // console.log('🔄 Element image changed, refreshing...');
        
        // Update the timestamp in localStorage
        if (element._id) {
          localStorage.setItem(`element_${element._id}_timestamp`, currentTimestamp.toString());
        }
        
        // Force image refresh
        const newKey = Date.now();
        setImageKey(newKey);
        setImageLoading(true);
        
        // Image cache and refresh handled by getImageUrl in imageUtils
        
        // Reset loading state after a short delay
        setTimeout(() => {
          setImageLoading(false);
        }, 100);
      }
    }
  }, [element.imageUrl, element.uploadTimestamp, element._id]);

  // Debug: Log element data to check if linked character fields are present
  useEffect(() => {
    // console.log('🔍 ElementCard data for:', element.name, {
    //   hasWeaponsOf: !!element.weaponsOf,
    //   weaponsOfLength: element.weaponsOf?.length || 0,
    //   hasArmorOf: !!element.armorOf,
    //   armorOfLength: element.armorOf?.length || 0,
    //   hasItemsOf: !!element.itemsOf,
    //   itemsOfLength: element.itemsOf?.length || 0,
    //   hasSpeciesOf: !!element.speciesOf,
    //   speciesOfLength: element.speciesOf?.length || 0,
    //   hasGenderOf: !!element.genderOf,
    //   genderOfLength: element.genderOf?.length || 0,
    //   hasBeliefsOf: !!element.beliefsOf,
    //   beliefsOfLength: element.beliefsOf?.length || 0,
    //   hasFamilyOf: !!element.familyOf,
    //   familyOfLength: element.familyOf?.length || 0,
    //   hasAffiliationsOf: !!element.affiliationsOf,
    //   affiliationsOfLength: element.affiliationsOf?.length || 0,
    //   hasAbilitiesOf: !!element.abilitiesOf,
    //   abilitiesOfLength: element.abilitiesOf?.length || 0
    // });
  }, [element._id, element.name, element.weaponsOf, element.armorOf, element.itemsOf, element.speciesOf, element.genderOf, element.beliefsOf, element.familyOf, element.affiliationsOf, element.abilitiesOf]);

  // Debug: Log active tab state changes
  useEffect(() => {
    // console.log('🔍 Active tab state:', activeTab);
  }, [activeTab]);

  // Debug: Log component render and tab availability
  useEffect(() => {
    // console.log('🔍 🔥 useEffect FIRED - element.name:', element.name, 'activeTab:', activeTab);
    // console.log('🔍 🔥 useEffect DEPENDENCIES - element.name:', element.name, 'activeTab:', activeTab);
    // console.log('🔍 🔥 useEffect CALLED AT:', new Date().toISOString());
    // console.log('🔍 ElementCard rendering for:', element.name, 'with tabs: Summary, Features, Attributes, Linked Characters');
    
    // Debug: Log all available tabs at the very beginning
    const availableTabs = [
      { name: 'summary', available: true },
      { name: 'features', available: true },
      { name: 'attributes', available: true },
      { name: 'linkedCharacters', available: true }
    ];
    
    // console.log('🔍 Available tabs for', element.name, ':', availableTabs.map(tab => tab.name).join(', '));
    // console.log('🔍 Active tab state:', activeTab);
    // console.log('🔍 Element has linked character data:', {
    //   weaponsOf: element.weaponsOf?.length || 0,
    //   armorOf: element.armorOf?.length || 0,
    //   itemsOf: element.itemsOf?.length || 0,
    //   speciesOf: element.speciesOf?.length || 0,
    //   genderOf: element.genderOf?.length || 0,
    //   beliefsOf: element.beliefsOf?.length || 0,
    //   familyOf: element.familyOf?.length || 0,
    //   affiliationsOf: element.affiliationsOf?.length || 0,
    //   abilitiesOf: element.abilitiesOf?.length || 0
    // });
  }, [element.name, activeTab]);

  // Debug: Log every render to see if element data is available
  useEffect(() => {
    // console.log('🔍 RENDER DEBUG - element received:', {
    //   name: element.name,
    //   id: element._id,
    //   hasWeaponsOf: !!element.weaponsOf,
    //   weaponsOfLength: element.weaponsOf?.length || 0
    // });
    // console.log('🔍 RENDER DEBUG - called at:', new Date().toISOString());
  });

  // Load custom templates from localStorage
  useEffect(() => {
    const loadCustomTemplates = () => {
      try {
        const stored = localStorage.getItem('elementCustomTemplates');
        if (stored) {
          const parsed = JSON.parse(stored);
          setCustomTemplates(parsed);
        }
      } catch (error) {
        console.error('Failed to load custom templates:', error);
      }
    };

    loadCustomTemplates();
    window.addEventListener('storage', loadCustomTemplates);
    return () => window.removeEventListener('storage', loadCustomTemplates);
  }, []);

  // Built-in templates for elements
  const articleTemplates = [
    {
      name: "Basic Element",
      content: "A fundamental element with basic properties and characteristics."
    },
    {
      name: "Advanced Element", 
      content: "A complex element with multiple properties, advanced features, and specialized usage patterns."
    },
    {
      name: "Rare Element",
      content: "A rare and valuable element with unique properties and limited availability."
    },
    {
      name: "Utility Element",
      content: "A practical element designed for specific use cases and applications."
    },
    {
      name: "Combat Element",
      content: "A powerful element optimized for combat scenarios and tactical situations."
    }
  ];

  const descriptionTemplates = [
    {
      name: "Detailed Description",
      content: "This element exhibits remarkable properties that make it highly versatile. Its unique characteristics allow for various applications across different domains."
    },
    {
      name: "Technical Specs",
      content: "Technical specifications include advanced features, optimized performance metrics, and enhanced functionality for specialized use cases."
    },
    {
      name: "Usage Guide",
      content: "This element is best utilized in specific scenarios where its properties can be maximized for optimal results and efficiency."
    }
  ];

  const handleEdit = (field: string, value: string) => {
    setEditingField(field);
    setEditValue(value);
    setShowSaveCancel(true);
  };

  const handleSave = async () => {
    if (!editingField) return;

    try {
      setLoading(true);
      const updatedElement = await elementsAPI.update(element._id, {
        [editingField]: editValue
      });
      
      onUpdate(updatedElement);
      if (editingField === 'article') {
        setEditValue(updatedElement.article || '');
      }
      setEditingField(null);
      setShowSaveCancel(false);
      
      // Show success toast
      if (typeof window.showToast === 'function') {
        window.showToast('Element updated successfully!', 'success');
      }
    } catch (error) {
      console.error('❌ Failed to update element:', error);
      
      // Show error toast
      if (typeof window.showToast === 'function') {
        window.showToast('Failed to update element. Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditingField(null);
    setShowSaveCancel(false);
  };

  const handleDelete = async () => {
    // Use React confirm modal
    const confirmDelete = await showConfirm('Are you sure you want to delete this element?', { type: 'warning' });
    
    if (!confirmDelete) return;
    
    try {
      await elementsAPI.delete(element._id);
      onDelete(element._id);
      // Show success toast if available
      if (typeof window.showToast === 'function') {
        window.showToast('Element deleted successfully!', 'success');
      }
    } catch (error) {
      console.error('Failed to delete element:', error);
      if (typeof window.showToast === 'function') {
        window.showToast('Failed to delete element.', 'error');
      }
    }
  };



  const handleImageClick = () => {
    setDroppedFile(null);
    setShowUploadModal(true);
  };

  const handleOpenSidePanel = (field: string, title: string) => {
    setSidePanelField(field);
    setSidePanelTitle(title);
    setShowSidePanel(true);
  };

  const handleSidePanelClose = () => {
    setShowSidePanel(false);
    setSidePanelField('');
    setSidePanelTitle('');
  };

  const handleSidePanelUpdate = async (updatedEntity: Element | Character | Pillar) => {
    // Only handle Element updates
    if ('category' in updatedEntity) {
      const updatedElement = updatedEntity as Element;
      // Refresh the element data to ensure linked elements are current
      try {
        setIsRefreshingLinks(true);
        const refreshedElement = await elementsAPI.getBySlug(element.slug);
        onUpdate(refreshedElement);
      } catch (error) {
        console.error('Failed to refresh element data:', error);
        // Fallback to the updated element from the side panel
        onUpdate(updatedElement);
      } finally {
        setIsRefreshingLinks(false);
      }
    } else {
      // For non-Element entities, just pass through
      onUpdate(updatedEntity as Element);
    }
  };

  const handleOpenTextEditModal = (field: string, title: string, content: string) => {
    setTextEditData({ field, title, content });
    setShowTextEditModal(true);
  };

  const handleTextEditModalClose = () => {
    setShowTextEditModal(false);
    setTextEditData(null);
  };

  const handleTextEditSave = async () => {
    if (!textEditData) return;

    try {
      setLoading(true);
      const updatedElement = await elementsAPI.update(element._id, {
        [textEditData.field]: textEditData.content
      });
      
      onUpdate(updatedElement);
      setShowTextEditModal(false);
      setTextEditData(null);
      
      if (window.showToast) {
        window.showToast('Element updated successfully!', 'success');
      }
    } catch (error) {
      console.error('Failed to update element:', error);
      if (window.showToast) {
        window.showToast('Failed to update element', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleModalTemplateSelect = (template: { name: string; content: string }) => {
    if (textEditData) {
      const currentContent = textEditData.content;
      const newContent = currentContent ? `${currentContent}\n\n${template.content}` : template.content;
      
      setTextEditData({
        ...textEditData,
        content: newContent
      });
    }
  };

  const handleCreateTemplate = (templateName: string, templateContent: string) => {
    if (!textEditData) return;

    const field = textEditData.field as 'article' | 'description';
    const newTemplate = { name: templateName, content: templateContent };
    
    setCustomTemplates(prev => {
      const updated = {
        ...prev,
        [field]: [...(prev[field] || []), newTemplate]
      };
      
      // Save to localStorage
      try {
        localStorage.setItem('elementCustomTemplates', JSON.stringify(updated));
      } catch (error) {
        console.error('Failed to save custom template:', error);
      }
      
      return updated;
    });
    
    setShowCreateTemplateModal(false);
    
    if (window.showToast) {
      window.showToast('Template created successfully!', 'success');
    }
  };

  const handleDeleteCustomTemplate = (field: 'article' | 'description', templateName: string) => {
    setCustomTemplates(prev => {
      const updated = {
        ...prev,
        [field]: (prev[field] || []).filter(t => t.name !== templateName)
      };
      
      // Save to localStorage
      try {
        localStorage.setItem('elementCustomTemplates', JSON.stringify(updated));
      } catch (error) {
        console.error('Failed to delete custom template:', error);
      }
      
      return updated;
    });
    
    if (window.showToast) {
      window.showToast('Template deleted successfully!', 'success');
    }
  };

  const refreshCustomTemplates = () => {
    try {
      const stored = localStorage.getItem('elementCustomTemplates');
      if (stored) {
        const parsed = JSON.parse(stored);
        setCustomTemplates(parsed);
      }
    } catch (error) {
      console.error('Failed to refresh custom templates:', error);
    }
  };

  const deduplicateTemplates = (templates: Array<{ name: string; content: string }>) => {
    const seen = new Set();
    return templates.filter(template => {
      const duplicate = seen.has(template.name);
      seen.add(template.name);
      return !duplicate;
    });
  };

  // Add toggle functions for sections and categories
  const toggleSection = (section: 'equipment' | 'details') => {
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const toggleCategory = (category: keyof typeof collapsedCategories) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length === 0) return;
    
    const file = files[0];
    
    if (!file.type.startsWith('image/')) {
      if (typeof window.showToast === 'function') {
        window.showToast('Please drop an image file.', 'error');
      }
      return;
    }
    
    // Set the dropped file and open upload modal
    setDroppedFile(file);
    setShowUploadModal(true);
  };

  const renderArrayField = (field: string, items: Element[], title: string) => (
    <div className="card-array__container">
      <button
        className="card-array__search-btn"
        type="button"
        aria-label={`Update ${title}`}
        onClick={() => handleOpenSidePanel(field, title)}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="darkorchid">
          <use xlinkHref="/img/icons.svg#icon-edit" />
        </svg>
        <span className="card-array__badge">{items.length}</span>
      </button>
      <div className="card-array__title-container">
        <h4 className="card-array__title--small">{title}</h4>
        {items && items.length > 0 ? (
          <button
            className="element-preview-btn"
            type="button"
            onClick={() => onOpenPreviewPanel(items.map(item => item._id))}
          >
            {items[0].name}
          </button>
        ) : (
          <span className="no-elements">NONE</span>
        )}
      </div>
    </div>
  );

  const renderLinkedElementsField = (items: Element[], title: string, isIncoming: boolean = false, showEditButton: boolean = false) => (
    <div className="card-array__container">
      {showEditButton && (
        <button
          className="card-array__search-btn"
          type="button"
          aria-label={`Update ${title}`}
          onClick={() => handleOpenSidePanel('links', title)}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="darkorchid">
            <use xlinkHref="/img/icons.svg#icon-edit" />
          </svg>
          <span className="card-array__badge">{items.length}</span>
        </button>
      )}
      <div className="card-array__title-container">
        <h4 className="card-array__title--small">
          {title}
          {isIncoming && <span className="incoming-indicator"> (Incoming)</span>}
        </h4>
        {isRefreshingLinks ? (
          <div className="loading-indicator">
                            <LoadingOverlay isLoading={true} message="Loading..." />
            <span>Refreshing...</span>
          </div>
        ) : items && items.length > 0 ? (
          <button
            className="element-preview-btn"
            type="button"
            onClick={() => onOpenPreviewPanel(items.map(item => item._id))}
          >
            {items[0].name}            
          </button>
        ) : (
          <span className="no-elements">NONE</span>
        )}
      </div>
    </div>
  );

  const renderPillarField = (pillars: any, title: string) => (
    <div className="card-array__container">
      <button
        className="card-array__search-btn"
        type="button"
        aria-label={`Update ${title}`}
        onClick={() => handleOpenSidePanel('pillar', title)}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="darkorchid">
          <use xlinkHref="/img/icons.svg#icon-edit" />
        </svg>
        <span className="card-array__badge">{pillars.length}</span>
      </button>
      <div className="card-array__title-container">
        <h4 className="card-array__title--small">{title}</h4>
        {pillars && pillars.length > 0 ? (
            <button
              className="element-preview-btn"
              type="button"
              onClick={() => onOpenPreviewPanel(pillars.map((p: any) => p._id))}
            >
              {(() => {
                const first = pillars[0];
                if (!first) return '';
                if (typeof first === 'string') return first;
                if (first && first.name) return first.name;
                return first._id || 'Unknown';
              })()}
            </button>
          ) : (
            <span className="no-elements">NONE</span>
          )}
      </div>
    </div>
  );

  const RARITY_ENUMS: string[] = ['common', 'uncommon', 'rare', 'legendary', 'mythic'];
  const RARITY_COLORS: Record<string, string> = {
    common: '#b0b0b0',
    uncommon: '#4caf50',
    rare: '#2196f3',
    legendary: '#ff9800',
    mythic: '#9c27b0',
  };

  // Add refs for each field at the top of the component
  const articleRef = useRef<HTMLDivElement>(null);
  const featuresRef = useRef<HTMLDivElement>(null);
  const attributesRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <div 
        className="element-card" 
        data-element-id={element._id}
        ref={cardRef}
        onMouseEnter={() => setShowDeleteBtn(true)}
        onMouseLeave={() => setShowDeleteBtn(false)}
        style={{ position: 'relative' }}
      >
        {/* Card loading overlay */}
        <div ref={overlayRef} />
        {/* Delete button */}
        {showDeleteBtn && (
          <button 
            className="card__delete-btn" 
            onClick={handleDelete}
            title="Delete element"
          >
            ×
          </button>
        )}

        <div className="card__header">
          <div 
            className="card__picture" 
            style={{ 
              position: 'relative', 
              cursor: 'pointer', 
              transition: 'all 0.3s ease',
              border: isDragging ? '3px dashed #55c57a' : '',
              backgroundColor: isDragging ? 'rgba(85, 197, 122, 0.1)' : ''
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            title={`Drag and drop an image here to replace ${element.name}'s picture`}
          >
            <OptimizedImage
              src={getImageSource(element, 'element', imageKey)}
              alt={element.name}
              className="card__picture-img"
              style={{ 
                opacity: uploadState || imageLoading ? 0.5 : 1,
                filter: uploadState || imageLoading ? 'grayscale(50%)' : 'none',
                cursor: 'pointer'
              }}
              onClick={handleImageClick}
              onLoad={() => {
                // console.log('✅ Image loaded successfully');
                setImageLoading(false);
              }}
              onError={() => handleImageError(setImageLoading, false)}
              dataAttributes={{
                'data-image-key': element.image || '',
                'data-element-id': element._id
              }}
            />
            <LoadingOverlay isLoading={loadingState?.isLoading ?? false} />
            <div 
              className="drag-overlay"
              style={{ 
                opacity: isDragging ? 1 : 0,
                borderColor: isDragging ? '#55c57a' : 'transparent'
              }}
            >
              <span style={{ color: '#55c57a', fontWeight: 'bold', fontSize: '0.9rem' }}>
                Drop image here
              </span>
            </div>
            <span 
              className="card__img-success" 
              style={{ 
                display: uploadState ? 'block' : 'none',
                opacity: uploadState ? 1 : 0
              }}
            >
              ✓
            </span>
          </div>

          <h3 className="heading-tertirary card__name-container">
            {editingField === 'name' ? (
              <>
                <input
                  type="text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="card__name-edit"
                />
                <div className="edit-buttons">
                  <button
                    className="btn btn--gold btn--xs save-btn"
                    onClick={handleSave}
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    className="btn btn--gray btn--xs cancel-btn"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <span
                className="card__name"
                contentEditable
                suppressContentEditableWarning
                data-element-id={element._id}
                data-original={element.name}
                data-field="name"
                onClick={() => {
                  setEditingField('name');
                  setEditValue(element.name);
                }}
                onBlur={(e) => handleEdit('name', e.currentTarget.textContent || '')}
              >
                {element.name}
              </span> 
            )}
          </h3>
        </div>

        <div className="card__details">
          <div className="tabs">
            <button
              className={`tab-btn${activeTab === 'summary' ? ' active' : ''}`}
              type="button"
              onClick={() => setActiveTab('summary')}
            >
              Summary
            </button>
            <button
              className={`tab-btn${activeTab === 'features' ? ' active' : ''}`}
              type="button"
              onClick={() => setActiveTab('features')}
            >
              Features
            </button>
            <button
              className={`tab-btn${activeTab === 'attributes' ? ' active' : ''}`}
              type="button"
              onClick={() => setActiveTab('attributes')}
            >
              Attributes
            </button>
            <button
              className={`tab-btn${activeTab === 'linkedCharacters' ? ' active' : ''}`}
              type="button"
              onClick={() => {
                console.log('🔍 Linked Characters tab clicked');
                setActiveTab('linkedCharacters');
              }}
            >
              Linked Characters
            </button>
          </div>

          {activeTab === 'summary' && (
            <div className="tab-content" id="tab-summary">
              <div className="tab-summary-flex-row">
                <div className="left-content">
                  <div className="card__text-container">
                    <div className="summary-header">
                      <h4 className="card__sub-heading">Article</h4>
                                      <div className="header-buttons">
                  {editingField === 'article' && showSaveCancel && (
                    <>
                      <div className="inline-edit-buttons">
                        <button
                          className="btn btn--gold btn--xs save-btn"
                          onClick={handleSave}
                          disabled={loading}
                        >
                          {loading ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          className="btn btn--gray btn--xs cancel-btn"
                          onClick={handleCancel}
                          disabled={loading}
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  )}
                  <button
                    className="text-edit-modal-btn"
                    onClick={() => handleOpenTextEditModal('article', 'Article', element.article || '')}
                    title="Full Edit (modal)"
                    style={{ marginTop: 8 }}
                  >
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                      <use xlinkHref="/img/icons.svg#icon-edit" />
                    </svg>
                    Full Edit
                  </button>
                      </div>
                    </div>
                    <div
                      ref={articleRef}
                      className={`card__text${editingField === 'article' ? ' editing' : ''}`}
                      contentEditable={editingField === 'article'}
                      suppressContentEditableWarning
                      data-original={element.article}
                      data-field="article"
                      onClick={e => {
                        if (editingField !== 'article') {
                          setEditingField('article');
                          setShowSaveCancel(true);
                          setTimeout(() => articleRef.current?.focus(), 0);
                        }
                      }}
                      onBlur={e => {
                        if (editingField === 'article') {
                          const newValue = e.currentTarget.textContent || '';
                          if (newValue !== element.article) {
                            setEditValue(newValue);
                            setShowSaveCancel(true);
                          } else {
                            setEditingField(null);
                            setShowSaveCancel(false);
                          }
                        }
                      }}
                      onKeyDown={e => {
                        if (editingField === 'article') {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            const newValue = (e.target as HTMLElement).textContent || '';
                            if (newValue !== element.article) {
                              setEditValue(newValue);
                              handleSave();
                            } else {
                              setEditingField(null);
                              setShowSaveCancel(false);
                            }
                          } else if (e.key === 'Escape') {
                            e.preventDefault();
                            setEditingField(null);
                            setShowSaveCancel(false);
                          }
                        }
                      }}
                      style={{ whiteSpace: 'pre-line', cursor: 'pointer' }}
                    >
                      {element.article || 'No article available. Click to edit.'}
                    </div>
                  </div>
                </div>
                <div className="right-content">
                  <div className="array-fields-container">
                    <div className="rarity-badge-container" style={{ position: 'relative' }}>
                      <div
                        ref={badgeRef}
                        className={`rarity-badge rarity-${(element.rarity || 'Common').toLowerCase()}`}
                        onClick={openRarityMenu}
                        style={{ cursor: 'pointer', zIndex: 2 }}
                        title="Click to change rarity"
                      >
                        <span className="rarity-text">{element.rarity || 'Common'}</span>
                      </div>
                    </div>
                    {renderPillarField(element.pillar || [], 'Pillar')}                    
                    {renderLinkedElementsField(element.links || [], 'Linked Elements', false, true)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'features' && (
            <div className="tab-content" id="tab-features">
              <div className="features-header">
                <h4 className="card__sub-heading">Features</h4>
                <div className="header-buttons">
                  {editingField === 'features' && showSaveCancel && (
                    <>
                      <div className="inline-edit-buttons">
                        <button
                          className="btn btn--gold btn--xs save-btn"
                          onClick={handleSave}
                          disabled={loading}
                        >
                          {loading ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          className="btn btn--gray btn--xs cancel-btn"
                          onClick={handleCancel}
                          disabled={loading}
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  )}
                  <button
                    className="text-edit-modal-btn"
                    onClick={() => handleOpenTextEditModal('features', 'Features', element.features || '')}
                    title="Full Edit (modal)"
                    style={{ marginTop: 8 }}
                  >
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                      <use xlinkHref="/img/icons.svg#icon-edit" />
                    </svg>
                    Full Edit
                  </button>
                </div>
              </div>
              <div
                ref={featuresRef}
                className={`card__text${editingField === 'features' ? ' editing' : ''}`}
                contentEditable={editingField === 'features'}
                suppressContentEditableWarning
                data-original={element.features}
                data-field="features"
                onClick={e => {
                  if (editingField !== 'features') {
                    setEditingField('features');
                    setShowSaveCancel(true);
                    setTimeout(() => featuresRef.current?.focus(), 0);
                  }
                }}
                onBlur={e => {
                  if (editingField === 'features') {
                    const newValue = e.currentTarget.textContent || '';
                    if (newValue !== element.features) {
                      setEditValue(newValue);
                      setShowSaveCancel(true);
                    } else {
                      setEditingField(null);
                      setShowSaveCancel(false);
                    }
                  }
                }}
                onKeyDown={e => {
                  if (editingField === 'features') {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      const newValue = (e.target as HTMLElement).textContent || '';
                      if (newValue !== element.features) {
                        setEditValue(newValue);
                        handleSave();
                      } else {
                        setEditingField(null);
                        setShowSaveCancel(false);
                      }
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      setEditingField(null);
                      setShowSaveCancel(false);
                    }
                  }
                }}
                style={{ whiteSpace: 'pre-line', cursor: 'pointer' }}
              >
                {element.features || 'No features available. Click to edit.'}
              </div>
            </div>
          )}

          {activeTab === 'attributes' && (
            <div className="tab-content" id="tab-attributes">
              <div className="attributes-header">
                <h4 className="card__sub-heading">Attributes</h4>
                <div className="header-buttons">
                  {editingField === 'attributes' && showSaveCancel && (
                    <>
                      <div className="inline-edit-buttons">
                        <button
                          className="btn btn--gold btn--xs save-btn"
                          onClick={handleSave}
                          disabled={loading}
                        >
                          {loading ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          className="btn btn--gray btn--xs cancel-btn"
                          onClick={handleCancel}
                          disabled={loading}
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  )}
                  <button
                    className="text-edit-modal-btn"
                    onClick={() => handleOpenTextEditModal('attributes', 'Attributes', element.attributes || '')}
                    title="Full Edit (modal)"
                    style={{ marginTop: 8 }}
                  >
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                      <use xlinkHref="/img/icons.svg#icon-edit" />
                    </svg>
                    Full Edit
                  </button>
                </div>
              </div>
              <div
                ref={attributesRef}
                className={`card__text${editingField === 'attributes' ? ' editing' : ''}`}
                contentEditable={editingField === 'attributes'}
                suppressContentEditableWarning
                data-original={element.attributes}
                data-field="attributes"
                onClick={e => {
                  if (editingField !== 'attributes') {
                    setEditingField('attributes');
                    setShowSaveCancel(true);
                    setTimeout(() => attributesRef.current?.focus(), 0);
                  }
                }}
                onBlur={e => {
                  if (editingField === 'attributes') {
                    const newValue = e.currentTarget.textContent || '';
                    if (newValue !== element.attributes) {
                      setEditValue(newValue);
                      setShowSaveCancel(true);
                    } else {
                      setEditingField(null);
                      setShowSaveCancel(false);
                    }
                  }
                }}
                onKeyDown={e => {
                  if (editingField === 'attributes') {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      const newValue = (e.target as HTMLElement).textContent || '';
                      if (newValue !== element.attributes) {
                        setEditValue(newValue);
                        handleSave();
                      } else {
                        setEditingField(null);
                        setShowSaveCancel(false);
                      }
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      setEditingField(null);
                      setShowSaveCancel(false);
                    }
                  }
                }}
                style={{ whiteSpace: 'pre-line', cursor: 'pointer' }}
              >
                {element.attributes || 'No attributes available. Click to edit.'}
              </div>
            </div>
          )}

          {activeTab === 'linkedCharacters' && (
            <div className="tab-content" id="tab-linked-characters">
              <div className="linked-characters-container">
                {/* Equipment Section */}
                <div className="linked-section">
                  <div className="section-header" onClick={() => toggleSection('equipment')}>                    
                    <span className="section-badge">
                      {(element.weaponsOf?.length || 0) + (element.armorOf?.length || 0) + (element.itemsOf?.length || 0)}
                    </span>                    
                    <svg 
                      className={`section-toggle ${collapsedSections.equipment ? 'collapsed' : ''}`}
                      width="16" 
                      height="16" 
                      viewBox="0 0 16 16"
                      fill="currentColor"
                    >
                      <path d="M4 6l4 4 4-4"/>
                    </svg>
                    <h4 className="card__sub-heading">Equipment</h4>
                  </div>
                  
                  {!collapsedSections.equipment && (
                    <>
                      {/* Weapons */}
                      <div className="character-category">
                        <div className="category-header" onClick={() => toggleCategory('weapons')}>                          
                          <span className="category-badge">{element.weaponsOf?.length || 0}</span>
                          <svg 
                            className={`category-toggle ${collapsedCategories.weapons ? 'collapsed' : ''}`}
                            width="12" 
                            height="12" 
                            viewBox="0 0 12 12"
                            fill="currentColor"
                          >
                            <path d="M3 4.5l3 3 3-3"/>
                          </svg>
                          <h5 className="character-category-title">Weapons</h5>
                          </div>
                          {!collapsedCategories.weapons && (
                          <div className="character-list">
                            {element.weaponsOf && element.weaponsOf.length > 0 ? (
                              element.weaponsOf.map(character => (
                                <Link 
                                  key={character._id} 
                                  to={`/character/${character.slug}`}
                                  className="character-link"
                                >
                                  <div className="character-item">
                                    <img 
                                      src={getImageSource(character, 'character', imageKey)} 
                                      alt={character.name}
                                      className="character-avatar"
                                      onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        if (target && target.src !== undefined) {
                                          target.src = 'https://aethermap-images.s3.amazonaws.com/characters/default-0.jpg';
                                        }
                                      }}
                                      onLoad={() => {
                                        // Image loaded successfully
                                      }}
                                    />
                                    <span className="character-name">{character.name}</span>
                                  </div>
                                </Link>
                              ))
                            ) : (
                              <span className="no-characters">No characters wield this element</span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Armor */}
                      <div className="character-category">
                        <div className="category-header" onClick={() => toggleCategory('armor')}>                          
                          <span className="category-badge">{element.armorOf?.length || 0}</span>
                          <svg 
                            className={`category-toggle ${collapsedCategories.armor ? 'collapsed' : ''}`}
                            width="12" 
                            height="12" 
                            viewBox="0 0 12 12"
                            fill="currentColor"
                          >
                            <path d="M3 4.5l3 3 3-3"/>
                          </svg>
                          <h5 className="character-category-title">Armor</h5>
                        </div>
                        {!collapsedCategories.armor && (
                          <div className="character-list">
                            {element.armorOf && element.armorOf.length > 0 ? (
                              element.armorOf.map(character => (
                                <Link 
                                  key={character._id} 
                                  to={`/character/${character.slug}`}
                                  className="character-link"
                                >
                                  <div className="character-item">
                                    <img 
                                      src={getImageSource(character, 'character', imageKey)} 
                                      alt={character.name}
                                      className="character-avatar"
                                      onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        if (target && target.src !== undefined) {
                                          target.src = 'https://aethermap-images.s3.amazonaws.com/characters/default-0.jpg';
                                        }
                                      }}
                                      onLoad={() => {
                                        // Image loaded successfully
                                      }}
                                    />
                                    <span className="character-name">{character.name}</span>
                                  </div>
                                </Link>
                              ))
                            ) : (
                              <span className="no-characters">No characters wear this element</span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Items */}
                      <div className="character-category">
                        <div className="category-header" onClick={() => toggleCategory('items')}>                          
                          <span className="category-badge">{element.itemsOf?.length || 0}</span>
                          <svg 
                            className={`category-toggle ${collapsedCategories.items ? 'collapsed' : ''}`}
                            width="12" 
                            height="12" 
                            viewBox="0 0 12 12"
                            fill="currentColor"
                          >
                            <path d="M3 4.5l3 3 3-3"/>
                          </svg>
                          <h5 className="character-category-title">Items</h5>
                        </div>
                        {!collapsedCategories.items && (
                          <div className="character-list">
                            {element.itemsOf && element.itemsOf.length > 0 ? (
                              element.itemsOf.map(character => (
                                <Link 
                                  key={character._id} 
                                  to={`/character/${character.slug}`}
                                  className="character-link"
                                >
                                  <div className="character-item">
                                    <img 
                                      src={getImageSource(character, 'character', imageKey)} 
                                      alt={character.name}
                                      className="character-avatar"
                                      onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        if (target && target.src !== undefined) {
                                          target.src = 'https://aethermap-images.s3.amazonaws.com/characters/default-0.jpg';
                                        }
                                      }}
                                      onLoad={() => {
                                        // Image loaded successfully
                                      }}
                                    />
                                    <span className="character-name">{character.name}</span>
                                  </div>
                                </Link>
                              ))
                            ) : (
                              <span className="no-characters">No characters carry this element</span>
                            )}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* Details Section */}
                <div className="linked-section">
                  <div className="section-header" onClick={() => toggleSection('details')}>
                    <span className="section-badge">
                      {(element.speciesOf?.length || 0) + (element.genderOf?.length || 0) + (element.beliefsOf?.length || 0) + (element.familyOf?.length || 0) + (element.affiliationsOf?.length || 0) + (element.abilitiesOf?.length || 0)}
                    </span>
                    <svg 
                      className={`section-toggle ${collapsedSections.details ? 'collapsed' : ''}`}
                      width="16" 
                      height="16" 
                      viewBox="0 0 16 16"
                      fill="currentColor"
                    >
                      <path d="M4 6l4 4 4-4"/>
                    </svg>
                    <h4 className="card__sub-heading">Details</h4>
                  </div>
                  
                  {!collapsedSections.details && (
                    <>
                      {/* Species */}
                      <div className="character-category">
                        <div className="category-header" onClick={() => toggleCategory('species')}>                          
                          <span className="category-badge">{element.speciesOf?.length || 0}</span>
                          <svg 
                            className={`category-toggle ${collapsedCategories.species ? 'collapsed' : ''}`}
                            width="12" 
                            height="12" 
                            viewBox="0 0 12 12"
                            fill="currentColor"
                          >
                            <path d="M3 4.5l3 3 3-3"/>
                          </svg>
                          <h5 className="character-category-title">Species</h5>
                        </div>
                        {!collapsedCategories.species && (
                          <div className="character-list">
                            {element.speciesOf && element.speciesOf.length > 0 ? (
                              element.speciesOf.map(character => (
                                <Link 
                                  key={character._id} 
                                  to={`/character/${character.slug}`}
                                  className="character-link"
                                >
                                  <div className="character-item">
                                    <img 
                                      src={getImageSource(character, 'character', imageKey)} 
                                      alt={character.name}
                                      className="character-avatar"
                                      onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        if (target && target.src !== undefined) {
                                          target.src = 'https://aethermap-images.s3.amazonaws.com/characters/default-0.jpg';
                                        }
                                      }}
                                      onLoad={() => {
                                        // Image loaded successfully
                                      }}
                                    />
                                    <span className="character-name">{character.name}</span>
                                  </div>
                                </Link>
                              ))
                            ) : (
                              <span className="no-characters">No characters of this species</span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Gender */}
                      <div className="character-category">
                        <div className="category-header" onClick={() => toggleCategory('gender')}>                          
                          <span className="category-badge">{element.genderOf?.length || 0}</span>
                          <svg 
                            className={`category-toggle ${collapsedCategories.gender ? 'collapsed' : ''}`}
                            width="12" 
                            height="12" 
                            viewBox="0 0 12 12"
                            fill="currentColor"
                          >
                            <path d="M3 4.5l3 3 3-3"/>
                          </svg>
                          <h5 className="character-category-title">Gender</h5>
                        </div>
                        {!collapsedCategories.gender && (
                          <div className="character-list">
                            {element.genderOf && element.genderOf.length > 0 ? (
                              element.genderOf.map(character => (
                                <Link 
                                  key={character._id} 
                                  to={`/character/${character.slug}`}
                                  className="character-link"
                                >
                                  <div className="character-item">
                                    <img 
                                      src={getImageSource(character, 'character', imageKey)} 
                                      alt={character.name}
                                      className="character-avatar"
                                      onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        if (target && target.src !== undefined) {
                                          target.src = 'https://aethermap-images.s3.amazonaws.com/characters/default-0.jpg';
                                        }
                                      }}
                                      onLoad={() => {
                                        // Image loaded successfully
                                      }}
                                    />
                                    <span className="character-name">{character.name}</span>
                                  </div>
                                </Link>
                              ))
                            ) : (
                              <span className="no-characters">No characters of this gender</span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Beliefs */}
                      <div className="character-category">
                          <div className="category-header" onClick={() => toggleCategory('beliefs')}>                          
                          <span className="category-badge">{element.beliefsOf?.length || 0}</span>
                          <svg 
                            className={`category-toggle ${collapsedCategories.beliefs ? 'collapsed' : ''}`}
                            width="12" 
                            height="12" 
                            viewBox="0 0 12 12"
                            fill="currentColor"
                          >
                            <path d="M3 4.5l3 3 3-3"/>
                          </svg>
                          <h5 className="character-category-title">Beliefs</h5>
                        </div>
                        {!collapsedCategories.beliefs && (
                          <div className="character-list">
                            {element.beliefsOf && element.beliefsOf.length > 0 ? (
                              element.beliefsOf.map(character => (
                                <Link 
                                  key={character._id} 
                                  to={`/character/${character.slug}`}
                                  className="character-link"
                                >
                                  <div className="character-item">
                                    <img 
                                      src={getImageSource(character, 'character', imageKey)} 
                                      alt={character.name}
                                      className="character-avatar"
                                      onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        if (target && target.src !== undefined) {
                                          target.src = 'https://aethermap-images.s3.amazonaws.com/characters/default-0.jpg';
                                        }
                                      }}
                                      onLoad={() => {
                                        // Image loaded successfully
                                      }}
                                    />
                                    <span className="character-name">{character.name}</span>
                                  </div>
                                </Link>
                              ))
                            ) : (
                              <span className="no-characters">No characters with these beliefs</span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Family */}
                      <div className="character-category">
                        <div className="category-header" onClick={() => toggleCategory('family')}>                          
                          <span className="category-badge">{element.familyOf?.length || 0}</span>
                          <svg 
                            className={`category-toggle ${collapsedCategories.family ? 'collapsed' : ''}`}
                            width="12" 
                            height="12" 
                            viewBox="0 0 12 12"
                            fill="currentColor"
                          >
                            <path d="M3 4.5l3 3 3-3"/>
                          </svg>
                          <h5 className="character-category-title">Family</h5>
                        </div>
                        {!collapsedCategories.family && (
                          <div className="character-list">
                            {element.familyOf && element.familyOf.length > 0 ? (
                              element.familyOf.map(character => (
                                <Link 
                                  key={character._id} 
                                  to={`/character/${character.slug}`}
                                  className="character-link"
                                >
                                  <div className="character-item">
                                    <img 
                                      src={getImageSource(character, 'character', imageKey)} 
                                      alt={character.name}
                                      className="character-avatar"
                                      onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        if (target && target.src !== undefined) {
                                          target.src = 'https://aethermap-images.s3.amazonaws.com/characters/default-0.jpg';
                                        }
                                      }}
                                      onLoad={() => {
                                        // Image loaded successfully
                                      }}
                                    />
                                    <span className="character-name">{character.name}</span>
                                  </div>
                                </Link>
                              ))
                            ) : (
                              <span className="no-characters">No characters in this family</span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Affiliations */}
                      <div className="character-category">
                        <div className="category-header" onClick={() => toggleCategory('affiliations')}>                          
                          <span className="category-badge">{element.affiliationsOf?.length || 0}</span>
                          <svg 
                            className={`category-toggle ${collapsedCategories.affiliations ? 'collapsed' : ''}`}
                            width="12" 
                            height="12" 
                            viewBox="0 0 12 12"
                            fill="currentColor"
                          >
                            <path d="M3 4.5l3 3 3-3"/>
                          </svg>
                          <h5 className="character-category-title">Affiliations</h5>
                        </div>
                        {!collapsedCategories.affiliations && (
                          <div className="character-list">
                            {element.affiliationsOf && element.affiliationsOf.length > 0 ? (
                              element.affiliationsOf.map(character => (
                                <Link 
                                  key={character._id} 
                                  to={`/character/${character.slug}`}
                                  className="character-link"
                                >
                                  <div className="character-item">
                                    <img 
                                      src={getImageSource(character, 'character', imageKey)} 
                                      alt={character.name}
                                      className="character-avatar"
                                      onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        if (target && target.src !== undefined) {
                                          target.src = 'https://aethermap-images.s3.amazonaws.com/characters/default-0.jpg';
                                        }
                                      }}
                                      onLoad={() => {
                                        // Image loaded successfully
                                      }}
                                    />
                                    <span className="character-name">{character.name}</span>
                                  </div>
                                </Link>
                              ))
                            ) : (
                              <span className="no-characters">No characters with these affiliations</span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Abilities */}
                      <div className="character-category">
                        <div className="category-header" onClick={() => toggleCategory('abilities')}>
                          <span className="category-badge">{element.abilitiesOf?.length || 0}</span>
                          <svg 
                            className={`category-toggle ${collapsedCategories.abilities ? 'collapsed' : ''}`}
                            width="12" 
                            height="12" 
                            viewBox="0 0 12 12"
                            fill="currentColor"
                          >
                            <path d="M3 4.5l3 3 3-3"/>
                          </svg>
                          <h5 className="character-category-title">Abilities</h5>
                          </div>
                        {!collapsedCategories.abilities && (
                          <div className="character-list">
                            {element.abilitiesOf && element.abilitiesOf.length > 0 ? (
                              element.abilitiesOf.map(character => (
                                <Link 
                                  key={character._id} 
                                  to={`/character/${character.slug}`}
                                  className="character-link"
                                >
                                  <div className="character-item">
                                    <img 
                                      src={getImageSource(character, 'character', imageKey)} 
                                      alt={character.name}
                                      className="character-avatar"
                                      onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        if (target && target.src !== undefined) {
                                          target.src = 'https://aethermap-images.s3.amazonaws.com/characters/default-0.jpg';
                                        }
                                      }}
                                      onLoad={() => {
                                        // Image loaded successfully
                                      }}
                                    />
                                    <span className="character-name">{character.name}</span>
                                  </div>
                                </Link>
                              ))
                            ) : (
                              <span className="no-characters">No characters with these abilities</span>
                            )}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="cardElem__footer">
          <Link className="btn btn--green btn--full details-btn" to={`/element/${element.slug}`}>
            Details
          </Link>
        </div>
      </div>

      {/* Confirm Modal */}
      {modalState && (
        <ConfirmModal
          isOpen={modalState.isOpen}
          message={modalState.message}
          type={modalState.type}
          okText={modalState.okText}
          cancelText={modalState.cancelText}
          onConfirm={handleConfirm}
          onCancel={handleModalCancel}
        />
      )}

      {/* Upload Image Modal */}
      <UploadImageModal
        isOpen={showUploadModal}
        characterName={element.name}
        initialFile={droppedFile || undefined}
        onUpload={handleImageUpload}
        onCancel={() => {
          setShowUploadModal(false);
          setDroppedFile(null);
        }}
      />

      {/* Side Panel */}
      <SidePanel
        isOpen={showSidePanel}
        onClose={handleSidePanelClose}
        entity={element}
        field={sidePanelField}
        title={sidePanelTitle}
        onUpdate={handleSidePanelUpdate}
      />

      {/* Text Edit Modal */}
      {showTextEditModal && textEditData && (
        <div className="modal-overlay" onClick={handleTextEditModalClose}>
          <div className="text-edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit {textEditData.title}</h3>
              <button 
                className="modal-close-btn"
                onClick={handleTextEditModalClose}
              >
                ×
              </button>
            </div>
            
            <div className="modal-content">
              <div className="modal-layout">
                {/* Template Column - 1/5 width */}
                <div className="modal-template-column">
                  <div className="template-column-header">
                    <h4 className="template-column-title">Templates</h4>
                    <button
                      className="create-template-btn"
                      onClick={() => setShowCreateTemplateModal(true)}
                      title="Create new template"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <use xlinkHref="/img/icons.svg#icon-plus" />
                      </svg>
                    </button>
                  </div>
                  <div className="template-list">
                    {/* Custom Templates (at the top) */}
                    {(() => {
                      const fieldKey = textEditData.field === 'article' || textEditData.field === 'description'
                        ? textEditData.field 
                        : 'article';
                      const fieldTemplates = customTemplates[fieldKey] || [];
                      
                      return fieldTemplates.map((template, index) => (
                        <div key={`custom-${index}`} className="custom-template-item">
                          <button
                            className="template-sidebar-option custom-template"
                            onClick={() => handleModalTemplateSelect(template)}
                          >
                            <span className="template-sidebar-name">{template.name}</span>
                            <span className="template-sidebar-preview">
                              {template.content.split('\n').slice(0, 3).join('\n')}
                              {template.content.split('\n').length > 3 ? '...' : ''}
                            </span>
                          </button>
                          <button
                            className="delete-template-btn"
                            onClick={() => handleDeleteCustomTemplate(fieldKey as 'article' | 'description', template.name)}
                            title="Delete template"
                          >
                            ×
                          </button>
                        </div>
                      ));
                    })()}
                    
                    {/* Built-in Templates (after custom templates) */}
                    {(textEditData.field === 'article' ? articleTemplates : 
                      textEditData.field === 'description' ? descriptionTemplates :
                      articleTemplates).map((template, index) => (
                      <button
                        key={`builtin-${index}`}
                        className="template-sidebar-option"
                        onClick={() => handleModalTemplateSelect(template)}
                      >
                        <span className="template-sidebar-name">{template.name}</span>
                        <span className="template-sidebar-preview">
                          {template.content.split('\n').slice(0, 3).join('\n')}
                          {template.content.split('\n').length > 3 ? '...' : ''}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Textarea Column - 4/5 width */}
                <div className="modal-textarea-column">
                  <div className="modal-textarea-container">
                    <textarea
                      className="text-edit-textarea"
                      value={textEditData.content}
                      onChange={(e) => setTextEditData({ ...textEditData, content: e.target.value })}
                      placeholder={`Enter ${textEditData.title.toLowerCase()}...`}
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button
                className="btn btn--gray"
                onClick={handleTextEditModalClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="btn btn--gold"
                onClick={handleTextEditSave}
                disabled={loading || !textEditData.content.trim()}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Template Modal */}
      {showCreateTemplateModal && textEditData && (
        <div className="modal-overlay" onClick={() => setShowCreateTemplateModal(false)}>
          <div className="create-template-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create New Template</h3>
              <button 
                className="modal-close-btn"
                onClick={() => setShowCreateTemplateModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-content">
              <CreateTemplateForm
                field={textEditData.field as 'article' | 'description'}
                currentContent={textEditData.content}
                onCreateTemplate={handleCreateTemplate}
                onCancel={() => setShowCreateTemplateModal(false)}
              />
            </div>
          </div>
        </div>
      )}

      {showRarityMenu && menuPosition && (
        <div
          className="radial-menu-overlay"
          onClick={() => setShowRarityMenu(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 9999,
            pointerEvents: 'auto',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              position: 'absolute',
              bottom: '20px',
              left: '50%',
              transform: 'translateX(-50%)',
              color: 'white',
              fontSize: '14px',
              opacity: 0.7,
              pointerEvents: 'none',
            }}
          >
            Click anywhere to cancel
          </div>
          <div
            className="rarity-radial-menu donut"
            onClick={e => e.stopPropagation()}
            style={{
              position: 'absolute',
              top: menuPosition.top,
              left: menuPosition.left,
              transform: 'translate(-50%, -50%)',
              zIndex: 10000,
              width: 100,
              height: 100,
              pointerEvents: 'auto',
              background: 'transparent',
            }}
          >
            <svg width="100" height="100" style={{ position: 'absolute', top: 0, left: 0, zIndex: 0, pointerEvents: 'none' }}>
              <defs>
                <filter id="blur-ring" x="-20%" y="-20%" width="140%" height="140%">
                  <feGaussianBlur stdDeviation="6" />
                </filter>
              </defs>
              <circle cx="50" cy="50" r="48" fill="rgba(255,255,255,0.18)" filter="url(#blur-ring)" />
              <circle cx="50" cy="50" r="32" fill="transparent" />
            </svg>
            <div className="radial-menu-inner" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
              {RARITY_ENUMS.map((rarity: string, i: number) => {
                const angle = (i / RARITY_ENUMS.length) * 2 * Math.PI;
                const radius = 38;
                const x = 50 + Math.cos(angle - Math.PI/2) * radius;
                const y = 50 + Math.sin(angle - Math.PI/2) * radius;
                return (
                  <button
                    key={rarity}
                    className={`radial-menu-item${element.rarity === rarity ? ' selected' : ''}`}
                    style={{
                      position: 'absolute',
                      left: x - 16,
                      top: y - 16,
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      border: element.rarity === rarity ? `2px solid ${RARITY_COLORS[rarity]}` : `2px solid rgba(120, 120, 180, 0.13)`,
                      background: RARITY_COLORS[rarity],
                      color: 'white',
                      fontWeight: 700,
                      cursor: 'pointer',
                      boxShadow: element.rarity === rarity ? `0 0 12px ${RARITY_COLORS[rarity]}` : '0 2px 8px rgba(80,80,120,0.10)',
                      transition: 'all 0.2s',
                      zIndex: 2,
                      fontSize: '1.1rem',
                      backdropFilter: 'blur(2px)',
                      WebkitBackdropFilter: 'blur(2px)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      // No rotation
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRaritySelect(rarity);
                    }}
                  >
                    {rarity.charAt(0).toUpperCase()}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ElementCard;  